@if (canView()) {
  <section
    class="cog-reading"
    [class.cog-reading--failed]="view().notice === 'failed'"
    aria-live="polite">
    <p class="cog-reading__label">
      <i class="pi pi-sparkles cog-reading__spark" aria-hidden="true"></i>
      <span>Leitura do Cog</span>
      @if (current(); as reading) {
        <span class="cog-reading__period"
          >· {{ reading.toPeriod }} contra {{ reading.fromPeriod }}</span
        >
      }
      @if (view().updating) {
        <span class="cog-reading__tag cog-reading__tag--attention">Atualizando</span>
      }
    </p>

    @if (loading()) {
      <div class="cog-reading__status">
        <app-cog-loading-spinner [size]="20" />
        <span>Buscando a leitura do Cog desta empresa.</span>
      </div>
    } @else if (loadFailed()) {
      <p class="cog-reading__text">
        Não conseguimos buscar a leitura do Cog agora. Os números da tela não dependem dela;
        recarregue a página daqui a alguns minutos.
      </p>
    } @else {
      @switch (view().notice) {
        @case ('none') {
          <div class="cog-reading__row">
            <p class="cog-reading__text">
              Esta empresa ainda não tem leitura do Cog. Ela é escrita sozinha quando um documento
              contábil é validado na Padronização; use Gerar agora para ler os números atuais.
            </p>
            @if (canGenerate()) {
              <div class="cog-reading__actions ignore-export">
                <app-button
                  label="Gerar agora"
                  icon="pi pi-sparkles"
                  size="small"
                  [fitContent]="true"
                  [isLoading]="requesting()"
                  loadingText="Pedindo..."
                  (sendClick)="generate()" />
              </div>
            }
          </div>
        }
        @case ('waiting') {
          <!-- waitingReason é o código do motivo (ex.: "documents"); o texto para a pessoa vem em message (Bug 3810).
               Documento retido para revisão segura a leitura até 24 h, então Gerar agora lê com o que já está validado. -->
          <div class="cog-reading__row">
            <p class="cog-reading__text">
              {{
                reading()?.message ||
                  'O Cog escreve a leitura assim que terminar a análise dos outros documentos contábeis desta empresa. Ela aparece aqui sozinha; use Gerar de novo para ler já com o que está validado.'
              }}
            </p>
            @if (canGenerate()) {
              <div class="cog-reading__actions ignore-export">
                <app-button
                  label="Gerar de novo"
                  icon="pi pi-refresh"
                  size="small"
                  [outlined]="true"
                  [fitContent]="true"
                  [isLoading]="requesting()"
                  loadingText="Pedindo..."
                  (sendClick)="generate()" />
              </div>
            }
          </div>
        }
        @case ('generating') {
          <div class="cog-reading__status">
            <app-cog-loading-spinner [size]="20" />
            <strong>O Cog está lendo os demonstrativos.</strong>
          </div>
          <p class="cog-reading__text">
            Leva cerca de 1 minuto. A leitura aparece aqui sozinha quando terminar; pode continuar
            usando a tela.
          </p>
        }
        @case ('insufficient') {
          <p class="cog-reading__text">
            A leitura do Cog aparece quando a empresa tiver pelo menos dois anos com DRE e Balanço
            validados.
            {{ reading()?.message || 'Valide outro ano na Padronização.' }}
          </p>
          @if (canOpenStandardization()) {
            <div class="cog-reading__actions ignore-export">
              <button type="button" class="cog-reading__link" (click)="openStandardization()">
                Abrir na Padronização
              </button>
            </div>
          }
        }
        @case ('failed') {
          <div class="cog-reading__row">
            <p class="cog-reading__text">
              Não conseguimos escrever a leitura agora. Os números da tela não mudam; use Gerar de
              novo daqui a alguns minutos.
            </p>
            @if (canGenerate()) {
              <div class="cog-reading__actions ignore-export">
                <app-button
                  label="Gerar de novo"
                  icon="pi pi-refresh"
                  size="small"
                  [outlined]="true"
                  [fitContent]="true"
                  [isLoading]="requesting()"
                  loadingText="Pedindo..."
                  (sendClick)="generate()" />
              </div>
            }
          </div>
        }
      }

      @if (view().showCurrent && current(); as reading) {
        @if (view().updating) {
          <p class="cog-reading__text">
            Esta leitura é de {{ reading.generatedAt | vaultDate }}, antes do último documento
            validado. O Cog já está escrevendo a nova; enquanto isso, a anterior continua visível
            embaixo, apagada.
          </p>
        }

        <div class="cog-reading__body" [class.cog-reading__body--dimmed]="view().updating">
          @if (reading.summary) {
            <p class="cog-reading__summary">{{ reading.summary }}</p>
          }

          @if (reading.findings.length) {
            <ul class="cog-reading__findings">
              @for (finding of reading.findings; track $index) {
                <li class="cog-reading__finding">
                  <span
                    [class]="'cog-reading__marker cog-reading__marker--' + finding.tone"
                    aria-hidden="true"></span>
                  <p class="cog-reading__finding-text">
                    <strong class="cog-reading__finding-title">{{ finding.title }}.</strong>
                    {{ finding.explanation }}
                    @if (finding.target !== 'highlights') {
                      <button
                        type="button"
                        class="cog-reading__link cog-reading__link--inline ignore-export"
                        (click)="openFinding(finding)">
                        {{ targetLinks[finding.target] }}
                      </button>
                    }
                  </p>
                </li>
              }
            </ul>
          }
        </div>

        <div class="cog-reading__footer">
          <span>
            Escrita pelo Cog em {{ reading.generatedAt | vaultDateTime
            }}{{
              reading.triggerSource === 'Manual'
                ? ', a pedido'
                : ', depois da validação de um documento contábil'
            }}
          </span>
          @if (canGenerate() && !view().updating && view().notice !== 'failed') {
            <button
              type="button"
              class="cog-reading__link ignore-export"
              [disabled]="requesting()"
              (click)="generate()">
              <i class="pi pi-refresh" aria-hidden="true"></i>
              {{ requesting() ? 'Pedindo...' : 'Gerar de novo' }}
            </button>
          }
        </div>
      }
    }
  </section>
}
